const TOKEN_STORAGE_KEY = "dicio.docs.tokens";
const AUTH_STATE_STORAGE_KEY = "dicio.docs.authState";

const defaultScopes = "openid profile";

export const authConfig = {
    domain: process.env.REACT_APP_COGNITO_DOMAIN || "",
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
    scopes: process.env.REACT_APP_COGNITO_SCOPES || defaultScopes,
};

const friendlyErrors = {
    access_denied: "El inicio de sesion fue cancelado o el usuario no tiene acceso.",
    invalid_request: "Cognito rechazo la solicitud de autenticacion.",
    unauthorized_client: "El App Client de Cognito no permite este flujo o redirect URL.",
    server_error: "Cognito no pudo completar el inicio de sesion.",
    temporarily_unavailable: "Cognito no esta disponible temporalmente.",
};

const toBase64Url = (buffer) =>
    btoa(String.fromCharCode(...new Uint8Array(buffer)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");

const randomString = (size = 48) => {
    const values = new Uint8Array(size);
    window.crypto.getRandomValues(values);
    return toBase64Url(values);
};

const sha256 = async (value) => {
    const data = new TextEncoder().encode(value);
    return window.crypto.subtle.digest("SHA-256", data);
};

const getRedirectUri = () => `${window.location.origin}/`;

const assertConfigured = () => {
    if (!authConfig.domain || !authConfig.clientId) {
        throw new Error(
            "Falta configurar REACT_APP_COGNITO_DOMAIN o REACT_APP_COGNITO_CLIENT_ID."
        );
    }
};

const parseJwtPayload = (token) => {
    try {
        const payload = token.split(".")[1];
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(normalized));
    } catch {
        return {};
    }
};

const normalizeError = (error, fallback) => {
    if (!error) return fallback;
    if (error instanceof Error) return error.message;
    return String(error);
};

export const getStoredTokens = () => {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;

    try {
        return JSON.parse(raw);
    } catch {
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
    }
};

export const storeTokens = (tokens) => {
    const accessPayload = parseJwtPayload(tokens.access_token);
    const idPayload = tokens.id_token ? parseJwtPayload(tokens.id_token) : {};

    window.localStorage.setItem(
        TOKEN_STORAGE_KEY,
        JSON.stringify({
            ...tokens,
            accessTokenExpiresAt: accessPayload.exp ? accessPayload.exp * 1000 : 0,
            username:
                idPayload["cognito:username"] ||
                idPayload.preferred_username ||
                idPayload.username ||
                accessPayload.username ||
                accessPayload.client_id ||
                "usuario",
        })
    );
};

export const clearStoredAuth = () => {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(AUTH_STATE_STORAGE_KEY);
};

export const hasValidAccessToken = (tokens = getStoredTokens()) =>
    Boolean(tokens?.access_token && tokens.accessTokenExpiresAt > Date.now() + 60000);

export const signIn = async () => {
    assertConfigured();

    const codeVerifier = randomString(64);
    const codeChallenge = toBase64Url(await sha256(codeVerifier));
    const state = randomString(32);
    const returnHash = window.location.hash || "#/";

    window.sessionStorage.setItem(
        AUTH_STATE_STORAGE_KEY,
        JSON.stringify({ codeVerifier, state, returnHash })
    );

    const params = new URLSearchParams({
        response_type: "code",
        client_id: authConfig.clientId,
        redirect_uri: getRedirectUri(),
        scope: authConfig.scopes,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
    });

    window.location.assign(`${authConfig.domain}/oauth2/authorize?${params.toString()}`);
};

export const exchangeCodeForTokens = async (code, codeVerifier) => {
    const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: authConfig.clientId,
        code,
        redirect_uri: getRedirectUri(),
        code_verifier: codeVerifier,
    });

    const response = await fetch(`${authConfig.domain}/oauth2/token`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(
            payload.error_description ||
                friendlyErrors[payload.error] ||
                `Cognito respondio ${response.status} al intercambiar el code.`
        );
    }

    storeTokens(payload);
    return payload;
};

export const refreshTokens = async (tokens = getStoredTokens()) => {
    if (!tokens?.refresh_token) {
        throw new Error("La sesion expiro y no hay refresh token disponible.");
    }

    const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: authConfig.clientId,
        refresh_token: tokens.refresh_token,
    });

    const response = await fetch(`${authConfig.domain}/oauth2/token`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        clearStoredAuth();
        throw new Error(
            payload.error_description ||
                friendlyErrors[payload.error] ||
                "No se pudo renovar la sesion en Cognito."
        );
    }

    const nextTokens = {
        ...tokens,
        ...payload,
        refresh_token: payload.refresh_token || tokens.refresh_token,
    };
    storeTokens(nextTokens);
    return getStoredTokens();
};

export const getAccessToken = async () => {
    const tokens = getStoredTokens();
    if (!tokens?.access_token) {
        throw new Error("No hay sesion activa.");
    }

    if (hasValidAccessToken(tokens)) {
        return tokens.access_token;
    }

    clearStoredAuth();
    throw new Error("Tu sesion expiro. Inicia sesion nuevamente para continuar.");
};

export const handleAuthRedirect = async () => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const code = params.get("code");
    const incomingState = params.get("state");

    if (error) {
        const description = params.get("error_description");
        window.history.replaceState({}, document.title, `${window.location.origin}/#/`);
        throw new Error(description || friendlyErrors[error] || `Error de Cognito: ${error}`);
    }

    if (!code) return null;

    const rawAuthState = window.sessionStorage.getItem(AUTH_STATE_STORAGE_KEY);
    if (!rawAuthState) {
        throw new Error("No se encontro el estado local de autenticacion. Inicia sesion de nuevo.");
    }

    const authState = JSON.parse(rawAuthState);
    if (incomingState !== authState.state) {
        clearStoredAuth();
        throw new Error("El estado de autenticacion no coincide. Se bloqueo el callback por seguridad.");
    }

    try {
        await exchangeCodeForTokens(code, authState.codeVerifier);
        window.sessionStorage.removeItem(AUTH_STATE_STORAGE_KEY);
        window.history.replaceState(
            {},
            document.title,
            `${window.location.origin}/${authState.returnHash || "#/"}`
        );
        return getStoredTokens();
    } catch (errorToNormalize) {
        throw new Error(
            normalizeError(
                errorToNormalize,
                "No se pudo completar el inicio de sesion con Cognito."
            )
        );
    }
};

export const signOut = () => {
    clearStoredAuth();

    if (!authConfig.domain || !authConfig.clientId) {
        window.location.assign("/");
        return;
    }

    const params = new URLSearchParams({
        client_id: authConfig.clientId,
        logout_uri: getRedirectUri(),
    });

    window.location.assign(`${authConfig.domain}/logout?${params.toString()}`);
};
